'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const NHL_TEAMS = [
  'ANA', 'ARI', 'BOS', 'BUF', 'CAR', 'CBJ', 'CGY', 'CHI',
  'COL', 'DAL', 'DET', 'EDM', 'FLA', 'LAK', 'MIN', 'MTL',
  'NJD', 'NSH', 'NYI', 'NYR', 'OTT', 'PHI', 'PIT', 'SEA',
  'SJS', 'STL', 'TBL', 'TOR', 'UTA', 'VAN', 'VGK', 'WSH',
];

const STAT_OPTIONS = [
  { id: 'xgoals', label: 'xGoals', category: 'Offensif' },
  { id: 'corsi', label: 'Corsi%', category: 'Possession' },
  { id: 'fenwick', label: 'Fenwick%', category: 'Possession' },
  { id: 'pp_pct', label: 'PP%', category: 'Spéciales' },
  { id: 'pk_pct', label: 'PK%', category: 'Spéciales' },
  { id: 'save_pct', label: 'SV%', category: 'Gardien' },
  { id: 'gaa', label: 'GAA', category: 'Gardien' },
  { id: 'win_prob', label: 'Win Prob', category: 'Modèle' },
  { id: 'confidence', label: 'Confiance', category: 'Modèle' },
  { id: 'home_away', label: 'Dom/Ext', category: 'Contexte' },
  { id: 'streak', label: 'Série', category: 'Contexte' },
  { id: 'fatigue', label: 'Fatigue', category: 'Contexte' },
];

const WIDGET_OPTIONS = [
  { id: 'kpis', label: 'KPIs principaux', icon: 'ChartBarIcon' },
  { id: 'featured_match', label: 'Match vedette', icon: 'StarIcon' },
  { id: 'alerts', label: 'Alertes actives', icon: 'BellAlertIcon' },
  { id: 'charts', label: 'Graphiques', icon: 'PresentationChartLineIcon' },
  { id: 'standings', label: 'Classement', icon: 'ListBulletIcon' },
  { id: 'live_games', label: 'Matchs live', icon: 'BoltIcon' },
  { id: 'model_accuracy', label: 'Précision modèle', icon: 'CpuChipIcon' },
  { id: 'goalie_stats', label: 'Stats gardiens', icon: 'ShieldCheckIcon' },
];

const HISTORY_ITEMS = [
  { page: 'Dashboard', action: 'Consulté', time: '21:45', icon: 'ChartBarIcon' },
  { page: 'Live Analytics — TOR vs BOS', action: 'Analysé', time: '21:30', icon: 'BoltIcon' },
  { page: 'ML Engine — Random Forest', action: 'Comparé', time: '21:15', icon: 'CpuChipIcon' },
  { page: 'Alertes — Matthews blessure', action: 'Lu', time: '20:58', icon: 'BellAlertIcon' },
  { page: 'Analyse — Colorado Avalanche', action: 'Analysé', time: '20:30', icon: 'MagnifyingGlassCircleIcon' },
  { page: 'Backtesting — Saison 2024-25', action: 'Consulté', time: '19:45', icon: 'ClockIcon' },
];

export default function PersonalizationClient() {
  const [favoriteTeams, setFavoriteTeams] = useState<string[]>(['TOR', 'EDM', 'COL']);
  const [preferredStats, setPreferredStats] = useState<string[]>(['xgoals', 'corsi', 'pp_pct', 'save_pct']);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(['kpis', 'featured_match', 'alerts', 'charts']);
  const [alertMinSeverity, setAlertMinSeverity] = useState('medium');
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [browserNotifs, setBrowserNotifs] = useState(true);
  const [discordNotifs, setDiscordNotifs] = useState(false);
  const [telegramNotifs, setTelegramNotifs] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggleTeam = (team: string) => {
    setFavoriteTeams(prev =>
      prev.includes(team) ? prev.filter(t => t !== team) : prev.length < 5 ? [...prev, team] : prev
    );
  };

  const toggleStat = (stat: string) => {
    setPreferredStats(prev =>
      prev.includes(stat) ? prev.filter(s => s !== stat) : [...prev, stat]
    );
  };

  const toggleWidget = (widget: string) => {
    setActiveWidgets(prev =>
      prev.includes(widget) ? prev.filter(w => w !== widget) : [...prev, widget]
    );
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">User Preference Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Personnalisez votre expérience analytique premium
          </p>
        </div>
        <button onClick={handleSave} className="btn-primary flex items-center gap-2">
          <Icon name={saved ? 'CheckIcon' : 'BookmarkIcon'} size={14} />
          {saved ? 'Sauvegardé !' : 'Sauvegarder'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Favorite teams */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="StarIcon" size={14} className="text-warning" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Équipes favorites</span>
              <span className="text-2xs text-muted-foreground ml-auto">{favoriteTeams.length}/5 sélectionnées</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {NHL_TEAMS.map((team) => (
                <button
                  key={team}
                  onClick={() => toggleTeam(team)}
                  className={`px-2.5 py-1 rounded text-xs font-mono-data font-semibold transition-all ${
                    favoriteTeams.includes(team)
                      ? 'bg-primary/20 text-primary border border-primary/40' :'bg-muted text-muted-foreground border border-border hover:border-primary/20'
                  }`}
                >
                  {team}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred stats */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ChartBarIcon" size={14} className="text-primary" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Statistiques préférées</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STAT_OPTIONS.map((stat) => (
                <button
                  key={stat.id}
                  onClick={() => toggleStat(stat.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded border text-xs transition-all ${
                    preferredStats.includes(stat.id)
                      ? 'bg-primary/10 border-primary/30 text-primary' :'bg-muted border-border text-muted-foreground hover:border-primary/20'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${preferredStats.includes(stat.id) ? 'bg-primary' : 'bg-muted-foreground'}`} />
                  <span className="font-medium">{stat.label}</span>
                  <span className="text-2xs opacity-60 ml-auto">{stat.category}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dashboard widgets */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Squares2X2Icon" size={14} className="text-accent" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Widgets dashboard</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {WIDGET_OPTIONS.map((widget) => (
                <button
                  key={widget.id}
                  onClick={() => toggleWidget(widget.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded border text-xs transition-all ${
                    activeWidgets.includes(widget.id)
                      ? 'bg-accent/10 border-accent/30 text-accent' :'bg-muted border-border text-muted-foreground hover:border-accent/20'
                  }`}
                >
                  <Icon name={widget.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                  <span className="text-center leading-tight">{widget.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Notification preferences */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="BellIcon" size={14} className="text-primary" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notifications</span>
            </div>
            <div className="space-y-3">
              {[
                { id: 'email', label: 'Email', icon: 'EnvelopeIcon', value: emailNotifs, setter: setEmailNotifs },
                { id: 'browser', label: 'Navigateur', icon: 'ComputerDesktopIcon', value: browserNotifs, setter: setBrowserNotifs },
                { id: 'discord', label: 'Discord', icon: 'ChatBubbleLeftRightIcon', value: discordNotifs, setter: setDiscordNotifs },
                { id: 'telegram', label: 'Telegram', icon: 'PaperAirplaneIcon', value: telegramNotifs, setter: setTelegramNotifs },
              ].map((notif) => (
                <div key={notif.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={notif.icon as Parameters<typeof Icon>[0]['name']} size={13} className={notif.value ? 'text-primary' : 'text-muted-foreground'} />
                    <span className="text-sm text-foreground">{notif.label}</span>
                  </div>
                  <button
                    onClick={() => notif.setter(!notif.value)}
                    className={`w-9 h-5 rounded-full transition-all relative ${notif.value ? 'bg-primary' : 'bg-muted border border-border'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow ${notif.value ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <p className="text-2xs text-muted-foreground mb-2">Sévérité minimale</p>
              <div className="flex gap-2">
                {['low', 'medium', 'high', 'critical'].map(s => (
                  <button
                    key={s}
                    onClick={() => setAlertMinSeverity(s)}
                    className={`flex-1 py-1 rounded text-2xs font-medium transition-all ${alertMinSeverity === s ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted text-muted-foreground border border-border'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Activity history */}
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="ClockIcon" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Historique activité</span>
            </div>
            <div className="space-y-2">
              {HISTORY_ITEMS.map((item, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-0">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={12} className="text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">{item.page}</p>
                    <p className="text-2xs text-muted-foreground">{item.action}</p>
                  </div>
                  <span className="text-2xs font-mono-data text-muted-foreground">{item.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Current preferences summary */}
          <div className="card p-4 bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="SparklesIcon" size={14} className="text-primary" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Profil actuel</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Équipes suivies</span>
                <span className="font-mono-data text-primary">{favoriteTeams.join(', ') || 'Aucune'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Stats actives</span>
                <span className="font-mono-data text-foreground">{preferredStats.length} stats</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Widgets actifs</span>
                <span className="font-mono-data text-foreground">{activeWidgets.length} widgets</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Alertes min.</span>
                <span className="font-mono-data text-warning capitalize">{alertMinSeverity}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
